import React from 'react';
import {connect} from 'react-redux';
import arrayMutators from 'final-form-arrays';
import {FormRenderProps} from 'react-final-form';
import {FieldArray} from 'react-final-form-arrays';
import {Button, Grid, IconButton, Typography} from '@mui/material';

import {Dispatch} from 'spectra-logic-ui';
import {updateResource} from 'spectra-logic-ui/actions';
import {FormDialog, SingleSelect, TextField} from 'spectra-logic-ui/components';

import {Action, UserAction} from '@/types';
import {IAMUser} from '@/iam/types';
import DeleteIcon from 'spectra-logic-ui/icons/Delete';

type Props = {
  user: IAMUser;
  userAction: UserAction;
  actions: Action[];
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<any>;
}

const validate = (values: any) => {
  const errors: Record<string, string> = {};
  if (!values.id) {
    errors.id = 'An action must be selected';
  }
  return errors;
};

const AssignActionDialog = ({userAction, actions = [], ...others}: Props) => {
  const options: any[] = actions.map((a) => {
    let title = a.title;
    if (a.version) {
      title += ` - Version ${a.version}`;
    }
    return {key: a.id, text: title};
  });

  const initialValues: Record<string, string | object> = {};
  if (userAction) {
    initialValues.id = userAction.id;
    if (userAction.args && userAction.args.length > 0) {
      initialValues.argFields = userAction.args.map((uaa) => ({value: uaa}));
    }
  }

  return (
    <FormDialog title='Assign Action to IAM User' submitLabel='Assign' initialValues={initialValues}
      mutators={{...arrayMutators}} validate={validate} {...others}>
      {({values}: FormRenderProps) => {
        const selectedActionId = values.id;
        let selectedAction: Action | undefined;
        if (selectedActionId) {
          selectedAction = actions.find((a) => a.id === selectedActionId);
        }
        return (
          <>
            <SingleSelect name='id' label='Action' options={options} />
            {selectedAction && <>
              <Typography sx={{paddingTop: '10px', paddingBottom: '10px'}}>{selectedAction.description}</Typography>
              <FieldArray name='argFields' component={ActionArg} />
            </>}
          </>
        );
      }}
    </FormDialog>
  );
};

// ActionArg is a component that handles the action arguments array. No client-side
// validation (e.g., we allow an empty argument) for maximum flexibility. For example,
// an action might require an argument like "-i ''", which would be handled as two
// arguments, the second of which would be empty.
const ActionArg = ({fields}: any) => {
  return (
    <>
      {fields.map((arg: string, index: number) => (
        <Grid key={index} container spacing={2}>
          <Grid item xs={11}>
            <TextField key={index} name={`${arg}.value`} label={`Argument ${index + 1}`} />
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={() => fields.remove(index)} size='large'>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button color='primary' onClick={() => fields.push({})}>Add Argument</Button>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch, {user, actions}: Props) => {
  return {
    onSubmit: ({id, argFields=[]}: {id: string; argFields: string[]}) => {
      const action = actions.find((a) => a.id === id) || {} as Action;
      const resource = `actions/${user.accountid}/${action.action}`;
      const args = argFields.map((argField: any) => argField.value);
      const body = {id: id, args: args};
      return dispatch(updateResource(resource, user.username, body));
    },
  };
};

export default connect(null, mapDispatchToProps)(AssignActionDialog);
